<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Integration from './Integration.vue';
import integrationAPI from 'dashboard/api/integrations';

import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

defineProps({
  error: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const purgeDialogRef = ref(null);
const integrationLoaded = ref(false);
const storeUrl = ref('');
const clientId = ref('');
const clientSecret = ref('');
const isSubmitting = ref(false);
const storeUrlError = ref('');
const clientIdError = ref('');
const clientSecretError = ref('');
const statusPayload = ref(null);
const statusError = ref('');
const statusLoaded = ref(false);
const isDisconnecting = ref(false);
const isPurgingCatalog = ref(false);
const isTestingConnection = ref(false);
const isSyncingCatalog = ref(false);
const isSyncingLocations = ref(false);
const isReplayingEnrichment = ref(false);
const isCancelingBulkOperation = ref(false);
const isRefreshingBulkOperation = ref(false);
const isTargetedResyncing = ref(false);
const replayEventType = ref('');
const replayMatchStatus = ref('');
const replaySince = ref('');
const replaySku = ref('');
const replayProductGid = ref('');
const replayVariantGid = ref('');
const targetedResyncOperation = ref('product_variant_inventory');
const targetedResyncQuery = ref('');
const targetedResyncGid = ref('');
const targetedResyncPageSize = ref('50');
const integration = useFunctionGetter('integrations/getIntegration', 'shopify');
const uiFlags = useMapGetter('integrations/getUIFlags');

const isConnected = computed(() => statusPayload.value?.connected === true);
const missingScopes = computed(() =>
  isConnected.value ? statusPayload.value?.missing_scopes || [] : []
);
const providerWorkReadyStatuses = ['connected', 'syncing'];
const isProviderReady = computed(() =>
  providerWorkReadyStatuses.includes(statusPayload.value?.status)
);
const canQueueShopifyProviderWork = computed(
  () => isProviderReady.value && missingScopes.value.length === 0
);
const grantedScopes = computed(() => statusPayload.value?.granted_scopes || []);
const grantedScopesLabel = computed(() =>
  grantedScopes.value.length
    ? grantedScopes.value.join(', ')
    : t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.NO_GRANTED_SCOPES')
);
const setupCallbackUrl = computed(
  () => statusPayload.value?.setup?.callback_url || ''
);
const setupRequiredScopes = computed(
  () => statusPayload.value?.setup?.required_scopes || []
);
const setupRequiredScopesLabel = computed(() =>
  setupRequiredScopes.value.length ? setupRequiredScopes.value.join(',') : ''
);
const catalogCounts = computed(() => ({
  products: statusPayload.value?.catalog_counts?.products || 0,
  variants: statusPayload.value?.catalog_counts?.variants || 0,
  inventoryItems: statusPayload.value?.catalog_counts?.inventory_items || 0,
  inventoryLevels: statusPayload.value?.catalog_counts?.inventory_levels || 0,
  locations: statusPayload.value?.catalog_counts?.locations || 0,
}));
const hasCatalogRows = computed(() =>
  Object.values(catalogCounts.value).some(count => count > 0)
);
const bulkOperations = computed(
  () =>
    statusPayload.value?.bulk_operations || {
      current: null,
      latest_successful: null,
      last_catalog_sync_at: null,
      last_locations_sync_at: null,
      latest_scheduled_skip: null,
    }
);
const recentSyncAttempts = computed(
  () => statusPayload.value?.recent_sync_attempts || []
);
const latestScheduledSkip = computed(
  () => bulkOperations.value.latest_scheduled_skip
);
const currentBulkOperation = computed(() => bulkOperations.value.current);
const hasActiveBulkOperation = computed(() =>
  Boolean(currentBulkOperation.value)
);
const canCancelCurrentBulkOperation = computed(
  () => currentBulkOperation.value?.cancelable === true
);
const canStartShopifyProviderWork = computed(
  () => canQueueShopifyProviderWork.value && !hasActiveBulkOperation.value
);
const replayEventTypeOptions = computed(() => [
  {
    value: 'viewed_product',
    label: t(
      'INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.EVENT_TYPES.viewed_product'
    ),
  },
  {
    value: 'added_to_cart',
    label: t(
      'INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.EVENT_TYPES.added_to_cart'
    ),
  },
  {
    value: 'ordered_product',
    label: t(
      'INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.EVENT_TYPES.ordered_product'
    ),
  },
  {
    value: 'placed_order',
    label: t(
      'INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.EVENT_TYPES.placed_order'
    ),
  },
]);
const replayMatchStatusOptions = computed(() => [
  {
    value: 'unmatched',
    label: t(
      'INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.MATCH_STATUSES.unmatched'
    ),
  },
  {
    value: 'ambiguous',
    label: t(
      'INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.MATCH_STATUSES.ambiguous'
    ),
  },
  {
    value: 'matched',
    label: t(
      'INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.MATCH_STATUSES.matched'
    ),
  },
]);

const integrationAction = computed(() => {
  if (isConnected.value) {
    return 'configure';
  }
  return 'connect';
});

/**
 * Formats the currently active Shopify bulk operation for compact status display.
 *
 * @param {Object|null} operation Bulk operation summary from the status API.
 * @returns {string} Human-readable operation status.
 */
const formatCurrentBulkOperation = operation => {
  if (!operation) {
    return t('INTEGRATION_SETTINGS.SHOPIFY.BULK_STATUS.NONE');
  }

  return `${operation.operation_type} - ${operation.provider_status}/${operation.import_status}`;
};

/**
 * Formats the latest successful Shopify bulk operation for compact status display.
 *
 * @param {Object|null} operation Bulk operation summary from the status API.
 * @returns {string} Human-readable operation status.
 */
const formatLatestSuccessfulBulkOperation = operation => {
  if (!operation) {
    return t('INTEGRATION_SETTINGS.SHOPIFY.BULK_STATUS.NONE');
  }

  return `${operation.operation_type} - ${operation.completed_at}`;
};

/**
 * Formats compact import-result counters for Shopify sync diagnostics.
 *
 * @param {Object|null} importResult Import result metadata from a sync attempt.
 * @returns {string} Human-readable result counters.
 */
const formatImportResult = importResult => {
  if (!importResult) {
    return '';
  }

  return Object.entries(importResult)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
};

/**
 * Folds an active-operation conflict response into the current dashboard state.
 *
 * @param {Object} error Axios-like error object from a Shopify provider-work API.
 * @returns {boolean} Whether a conflict operation was applied.
 */
const applyActiveOperationConflict = error => {
  const activeOperation = error.response?.data?.active_operation;
  if (!activeOperation) {
    return false;
  }

  const currentPayload = statusPayload.value || {};
  const currentBulkOperations = currentPayload.bulk_operations || {};
  statusPayload.value = {
    ...currentPayload,
    bulk_operations: {
      current: activeOperation,
      latest_successful: currentBulkOperations.latest_successful || null,
      last_catalog_sync_at: currentBulkOperations.last_catalog_sync_at || null,
      last_locations_sync_at:
        currentBulkOperations.last_locations_sync_at || null,
      latest_scheduled_skip:
        currentBulkOperations.latest_scheduled_skip || null,
    },
  };
  return true;
};

/**
 * Handles Shopify provider-work API errors and applies recoverable conflict state.
 *
 * @param {Object} error Axios-like error object.
 * @param {string} fallbackMessage Fallback translated alert message.
 * @returns {void}
 */
const alertShopifyProviderError = (error, fallbackMessage) => {
  applyActiveOperationConflict(error);
  useAlert(error.response?.data?.error || error.message || fallbackMessage);
};

const hideStoreUrlModal = () => {
  storeUrl.value = '';
  clientId.value = '';
  clientSecret.value = '';
  storeUrlError.value = '';
  clientIdError.value = '';
  clientSecretError.value = '';
  isSubmitting.value = false;
};

/**
 * Checks that the merchant entered a Shopify-owned canonical store domain.
 *
 * @param {string} url Store URL entered by the operator.
 * @returns {boolean} Whether the domain can be sent to Shopify OAuth.
 */
const validateStoreUrl = url => {
  const pattern = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/;
  return pattern.test(url);
};

/**
 * Validates the local connection form before starting OAuth.
 *
 * @returns {boolean} Whether all required Shopify connection fields are ready.
 */
const validateConnectionForm = () => {
  storeUrlError.value = '';
  clientIdError.value = '';
  clientSecretError.value = '';

  if (!validateStoreUrl(storeUrl.value.trim())) {
    storeUrlError.value = t('INTEGRATION_SETTINGS.SHOPIFY.STORE_URL.INVALID');
  }

  if (!clientId.value.trim()) {
    clientIdError.value = t(
      'INTEGRATION_SETTINGS.SHOPIFY.CREDENTIALS.CLIENT_ID_REQUIRED'
    );
  }

  if (!clientSecret.value.trim()) {
    clientSecretError.value = t(
      'INTEGRATION_SETTINGS.SHOPIFY.CREDENTIALS.CLIENT_SECRET_REQUIRED'
    );
  }

  return (
    !storeUrlError.value && !clientIdError.value && !clientSecretError.value
  );
};

const openStoreUrlDialog = () => {
  if (dialogRef.value) {
    dialogRef.value.open();
  }
};

/**
 * Opens the explicit Shopify catalog purge confirmation dialog.
 *
 * @returns {void}
 */
const openPurgeCatalogDialog = () => {
  if (purgeDialogRef.value) {
    purgeDialogRef.value.open();
  }
};

const handleStoreUrlSubmit = async () => {
  try {
    if (!validateConnectionForm()) {
      return;
    }

    isSubmitting.value = true;
    const { data } = await integrationAPI.connectShopify({
      shopDomain: storeUrl.value.trim().toLowerCase(),
      clientId: clientId.value.trim(),
      clientSecret: clientSecret.value.trim(),
    });

    if (data.redirect_url) {
      window.location.href = data.redirect_url;
    }
  } catch (error) {
    storeUrlError.value =
      error.response?.data?.error ||
      error.message ||
      t('INTEGRATION_SETTINGS.SHOPIFY.ERROR');
  } finally {
    isSubmitting.value = false;
  }
};

/**
 * Loads dashboard-safe Shopify connection status for this account.
 *
 * @returns {Promise<void>}
 */
const loadShopifyStatus = async () => {
  try {
    statusError.value = '';
    const { data } = await integrationAPI.getShopifyStatus();
    statusPayload.value = data;
  } catch (error) {
    statusError.value =
      error.response?.data?.error ||
      error.message ||
      t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.ERROR');
  } finally {
    statusLoaded.value = true;
  }
};

/**
 * Disconnects the account-scoped Shopify commerce integration.
 *
 * @returns {Promise<void>}
 */
const disconnectShopify = async () => {
  if (isDisconnecting.value) {
    return;
  }

  try {
    isDisconnecting.value = true;
    const { data } = await integrationAPI.disconnectShopify();
    statusPayload.value = data;
    await store.dispatch('integrations/get', 'shopify');
    useAlert(t('INTEGRATION_SETTINGS.SHOPIFY.DISCONNECT.SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('INTEGRATION_SETTINGS.SHOPIFY.DISCONNECT.ERROR')
    );
  } finally {
    isDisconnecting.value = false;
  }
};

/**
 * Explicitly hard-deletes locally cached Shopify catalog rows.
 *
 * @returns {Promise<void>}
 */
const purgeShopifyCatalog = async () => {
  if (isPurgingCatalog.value) {
    return;
  }

  try {
    isPurgingCatalog.value = true;
    const { data } = await integrationAPI.purgeShopifyCatalog();
    statusPayload.value = data.status;
    useAlert(t('INTEGRATION_SETTINGS.SHOPIFY.PURGE.SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('INTEGRATION_SETTINGS.SHOPIFY.PURGE.ERROR')
    );
  } finally {
    isPurgingCatalog.value = false;
  }
};

/**
 * Runs the Shopify GraphQL connection test and refreshes status metadata.
 *
 * @returns {Promise<void>}
 */
const testShopifyConnection = async () => {
  if (isTestingConnection.value) {
    return;
  }

  try {
    isTestingConnection.value = true;
    const { data } = await integrationAPI.testShopify();
    statusPayload.value = data;
    useAlert(t('INTEGRATION_SETTINGS.SHOPIFY.TEST.SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('INTEGRATION_SETTINGS.SHOPIFY.TEST.ERROR')
    );
  } finally {
    isTestingConnection.value = false;
  }
};

/**
 * Queues a Shopify catalog bulk snapshot from the settings page.
 *
 * @returns {Promise<void>}
 */
const syncShopifyCatalog = async () => {
  if (!canStartShopifyProviderWork.value) {
    return;
  }

  try {
    isSyncingCatalog.value = true;
    const { data } = await integrationAPI.syncShopifyCatalog();
    statusPayload.value = data;
    useAlert(t('INTEGRATION_SETTINGS.SHOPIFY.SYNC_CATALOG.SUCCESS'));
  } catch (error) {
    alertShopifyProviderError(
      error,
      t('INTEGRATION_SETTINGS.SHOPIFY.SYNC_CATALOG.ERROR')
    );
  } finally {
    isSyncingCatalog.value = false;
  }
};

/**
 * Queues a Shopify locations bulk snapshot from the settings page.
 *
 * @returns {Promise<void>}
 */
const syncShopifyLocations = async () => {
  if (!canStartShopifyProviderWork.value) {
    return;
  }

  try {
    isSyncingLocations.value = true;
    const { data } = await integrationAPI.syncShopifyLocations();
    statusPayload.value = data;
    useAlert(t('INTEGRATION_SETTINGS.SHOPIFY.SYNC_LOCATIONS.SUCCESS'));
  } catch (error) {
    alertShopifyProviderError(
      error,
      t('INTEGRATION_SETTINGS.SHOPIFY.SYNC_LOCATIONS.ERROR')
    );
  } finally {
    isSyncingLocations.value = false;
  }
};

/**
 * Queues replay of parsed Shopify-shaped CRM events against cached catalog data.
 *
 * @returns {Promise<void>}
 */
const replayShopifyEnrichment = async () => {
  if (!isProviderReady.value) {
    return;
  }

  try {
    isReplayingEnrichment.value = true;
    const { data } = await integrationAPI.replayShopifyEnrichment({
      eventType: replayEventType.value,
      matchStatus: replayMatchStatus.value,
      since: replaySince.value.trim(),
      sku: replaySku.value.trim(),
      productGid: replayProductGid.value.trim(),
      variantGid: replayVariantGid.value.trim(),
    });
    statusPayload.value = data;
    useAlert(t('INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.ERROR')
    );
  } finally {
    isReplayingEnrichment.value = false;
  }
};

/**
 * Requests cancellation for the currently active Shopify bulk operation.
 *
 * @returns {Promise<void>}
 */
const cancelCurrentBulkOperation = async () => {
  if (isCancelingBulkOperation.value || !canCancelCurrentBulkOperation.value) {
    return;
  }

  try {
    isCancelingBulkOperation.value = true;
    const { data } = await integrationAPI.cancelShopifyBulkOperation(
      currentBulkOperation.value.id
    );
    statusPayload.value = data;
    useAlert(t('INTEGRATION_SETTINGS.SHOPIFY.CANCEL_BULK_OPERATION.SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('INTEGRATION_SETTINGS.SHOPIFY.CANCEL_BULK_OPERATION.ERROR')
    );
  } finally {
    isCancelingBulkOperation.value = false;
  }
};

/**
 * Queues a provider-state refresh for the current Shopify bulk operation.
 *
 * @returns {Promise<void>}
 */
const refreshCurrentBulkOperation = async () => {
  if (isRefreshingBulkOperation.value || !currentBulkOperation.value?.id) {
    return;
  }

  try {
    isRefreshingBulkOperation.value = true;
    const { data } = await integrationAPI.refreshShopifyBulkOperation(
      currentBulkOperation.value.id
    );
    statusPayload.value = data;
    useAlert(t('INTEGRATION_SETTINGS.SHOPIFY.REFRESH_BULK_OPERATION.SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('INTEGRATION_SETTINGS.SHOPIFY.REFRESH_BULK_OPERATION.ERROR')
    );
  } finally {
    isRefreshingBulkOperation.value = false;
  }
};

/**
 * Queues a targeted Shopify resync using operator-selected search criteria.
 *
 * @returns {Promise<void>}
 */
const targetedShopifyResync = async () => {
  if (!canStartShopifyProviderWork.value) {
    return;
  }

  try {
    isTargetedResyncing.value = true;
    const parsedPageSize = Number.parseInt(targetedResyncPageSize.value, 10);
    const pageSize = Number.isNaN(parsedPageSize)
      ? undefined
      : Math.min(Math.max(parsedPageSize, 1), 250);

    const { data } = await integrationAPI.targetedShopifyResync({
      operationType: targetedResyncOperation.value,
      query: targetedResyncQuery.value.trim(),
      gid: targetedResyncGid.value.trim(),
      pageSize,
    });
    statusPayload.value = data;
    useAlert(t('INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.SUCCESS'));
  } catch (error) {
    alertShopifyProviderError(
      error,
      t('INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.ERROR')
    );
  } finally {
    isTargetedResyncing.value = false;
  }
};

const initializeShopifyIntegration = async () => {
  await Promise.all([
    store.dispatch('integrations/get', 'shopify'),
    loadShopifyStatus(),
  ]);
  integrationLoaded.value = true;
};

onMounted(() => {
  initializeShopifyIntegration();
});
</script>

<template>
  <SettingsLayout
    :is-loading="
      !integrationLoaded || !statusLoaded || uiFlags.isCreatingShopify
    "
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.SHOPIFY.HEADER')"
        description=""
        feature-name="shopify_integration"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <Integration
          :integration-id="integration.id"
          :integration-logo="integration.logo"
          :integration-name="integration.name"
          :integration-description="integration.description"
          :integration-enabled="isConnected"
          :integration-action="integrationAction"
          :delete-confirmation-text="{
            title: t('INTEGRATION_SETTINGS.SHOPIFY.DELETE.TITLE'),
            message: t('INTEGRATION_SETTINGS.SHOPIFY.DELETE.MESSAGE'),
          }"
        >
          <template #action>
            <Button
              teal
              :label="t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
              @click="openStoreUrlDialog"
            />
          </template>
        </Integration>
        <section
          class="flex flex-col gap-4 p-5 outline outline-n-container outline-1 bg-n-card rounded-xl"
        >
          <div class="flex flex-col gap-1">
            <h3 class="mb-0 text-heading-2 text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.TITLE') }}
            </h3>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.DESCRIPTION') }}
            </p>
          </div>
          <div
            v-if="statusError"
            class="p-3 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
          >
            {{ statusError }}
          </div>
          <div
            v-if="statusPayload?.pending_rotation"
            class="p-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
            data-testid="shopify-pending-rotation"
          >
            <span class="font-medium">
              {{ t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.PENDING_ROTATION') }}
            </span>
            <span v-if="statusPayload.masked_pending_client_id">
              {{ statusPayload.masked_pending_client_id }}
            </span>
          </div>
          <dl
            v-if="statusPayload"
            class="grid grid-cols-1 gap-3 text-sm md:grid-cols-2"
          >
            <div>
              <dt class="text-n-slate-11">
                {{ t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.STATE') }}
              </dt>
              <dd class="m-0 font-medium text-n-slate-12">
                {{ statusPayload.status }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.SHOP') }}
              </dt>
              <dd class="m-0 font-medium text-n-slate-12">
                {{
                  statusPayload.shop_domain ||
                  t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.NOT_CONNECTED')
                }}
              </dd>
            </div>
            <div v-if="statusPayload.shop_name">
              <dt class="text-n-slate-11">
                {{ t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.SHOP_NAME') }}
              </dt>
              <dd class="m-0 font-medium text-n-slate-12">
                {{ statusPayload.shop_name }}
              </dd>
            </div>
            <div v-if="statusPayload.shop_currency_code">
              <dt class="text-n-slate-11">
                {{ t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.SHOP_CURRENCY') }}
              </dt>
              <dd class="m-0 font-medium text-n-slate-12">
                {{ statusPayload.shop_currency_code }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.API_REVISION') }}
              </dt>
              <dd class="m-0 font-medium text-n-slate-12">
                {{ statusPayload.api_revision }}
              </dd>
            </div>
            <div v-if="statusPayload.credential_mode">
              <dt class="text-n-slate-11">
                {{ t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.CREDENTIAL_MODE') }}
              </dt>
              <dd class="m-0 font-medium text-n-slate-12">
                {{ statusPayload.credential_mode }}
              </dd>
            </div>
            <div v-if="statusPayload.masked_client_id">
              <dt class="text-n-slate-11">
                {{ t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.CLIENT_ID') }}
              </dt>
              <dd class="m-0 font-medium text-n-slate-12">
                {{ statusPayload.masked_client_id }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.GRANTED_SCOPES') }}
              </dt>
              <dd class="m-0 font-medium text-n-slate-12">
                {{ grantedScopesLabel }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.LAST_TESTED') }}
              </dt>
              <dd class="m-0 font-medium text-n-slate-12">
                {{
                  statusPayload.last_tested_at ||
                  t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.NEVER_TESTED')
                }}
              </dd>
            </div>
          </dl>
          <div
            v-if="statusPayload"
            class="grid grid-cols-1 gap-4 md:grid-cols-2"
          >
            <div class="flex flex-col gap-3">
              <h4 class="mb-0 text-sm font-medium text-n-slate-12">
                {{ t('INTEGRATION_SETTINGS.SHOPIFY.CATALOG_COUNTS.TITLE') }}
              </h4>
              <dl class="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt class="text-n-slate-11">
                    {{
                      t('INTEGRATION_SETTINGS.SHOPIFY.CATALOG_COUNTS.PRODUCTS')
                    }}
                  </dt>
                  <dd class="m-0 font-medium text-n-slate-12">
                    {{ catalogCounts.products }}
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-11">
                    {{
                      t('INTEGRATION_SETTINGS.SHOPIFY.CATALOG_COUNTS.VARIANTS')
                    }}
                  </dt>
                  <dd class="m-0 font-medium text-n-slate-12">
                    {{ catalogCounts.variants }}
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-11">
                    {{
                      t(
                        'INTEGRATION_SETTINGS.SHOPIFY.CATALOG_COUNTS.INVENTORY_ITEMS'
                      )
                    }}
                  </dt>
                  <dd class="m-0 font-medium text-n-slate-12">
                    {{ catalogCounts.inventoryItems }}
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-11">
                    {{
                      t(
                        'INTEGRATION_SETTINGS.SHOPIFY.CATALOG_COUNTS.INVENTORY_LEVELS'
                      )
                    }}
                  </dt>
                  <dd class="m-0 font-medium text-n-slate-12">
                    {{ catalogCounts.inventoryLevels }}
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-11">
                    {{
                      t('INTEGRATION_SETTINGS.SHOPIFY.CATALOG_COUNTS.LOCATIONS')
                    }}
                  </dt>
                  <dd class="m-0 font-medium text-n-slate-12">
                    {{ catalogCounts.locations }}
                  </dd>
                </div>
              </dl>
            </div>
            <div class="flex flex-col gap-3">
              <h4 class="mb-0 text-sm font-medium text-n-slate-12">
                {{ t('INTEGRATION_SETTINGS.SHOPIFY.BULK_STATUS.TITLE') }}
              </h4>
              <dl class="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <dt class="text-n-slate-11">
                    {{ t('INTEGRATION_SETTINGS.SHOPIFY.BULK_STATUS.CURRENT') }}
                  </dt>
                  <dd class="m-0 font-medium text-n-slate-12">
                    {{ formatCurrentBulkOperation(bulkOperations.current) }}
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-11">
                    {{
                      t(
                        'INTEGRATION_SETTINGS.SHOPIFY.BULK_STATUS.LATEST_SUCCESSFUL'
                      )
                    }}
                  </dt>
                  <dd class="m-0 font-medium text-n-slate-12">
                    {{
                      formatLatestSuccessfulBulkOperation(
                        bulkOperations.latest_successful
                      )
                    }}
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-11">
                    {{
                      t(
                        'INTEGRATION_SETTINGS.SHOPIFY.BULK_STATUS.LAST_CATALOG_SYNC'
                      )
                    }}
                  </dt>
                  <dd class="m-0 font-medium text-n-slate-12">
                    {{
                      bulkOperations.last_catalog_sync_at ||
                      t('INTEGRATION_SETTINGS.SHOPIFY.BULK_STATUS.NEVER')
                    }}
                  </dd>
                </div>
                <div>
                  <dt class="text-n-slate-11">
                    {{
                      t(
                        'INTEGRATION_SETTINGS.SHOPIFY.BULK_STATUS.LAST_LOCATIONS_SYNC'
                      )
                    }}
                  </dt>
                  <dd class="m-0 font-medium text-n-slate-12">
                    {{
                      bulkOperations.last_locations_sync_at ||
                      t('INTEGRATION_SETTINGS.SHOPIFY.BULK_STATUS.NEVER')
                    }}
                  </dd>
                </div>
              </dl>
              <div
                v-if="latestScheduledSkip"
                class="p-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
              >
                <span class="font-medium">
                  {{
                    t('INTEGRATION_SETTINGS.SHOPIFY.BULK_STATUS.SCHEDULED_SKIP')
                  }}
                </span>
                {{ latestScheduledSkip.operation_type }}
                <span v-if="latestScheduledSkip.created_at">
                  {{
                    t(
                      'INTEGRATION_SETTINGS.SHOPIFY.BULK_STATUS.SCHEDULED_SKIP_AT',
                      { timestamp: latestScheduledSkip.created_at }
                    )
                  }}
                </span>
              </div>
              <div
                v-if="currentBulkOperation?.stale"
                class="p-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
              >
                <span class="font-medium">
                  {{ t('INTEGRATION_SETTINGS.SHOPIFY.BULK_STATUS.STALE') }}
                </span>
                {{ currentBulkOperation.operation_type }}
              </div>
            </div>
          </div>
          <div v-if="recentSyncAttempts.length" class="flex flex-col gap-3">
            <h4 class="mb-0 text-sm font-medium text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.SHOPIFY.SYNC_ATTEMPTS.TITLE') }}
            </h4>
            <div class="grid grid-cols-1 gap-2">
              <div
                v-for="attempt in recentSyncAttempts"
                :key="attempt.id"
                class="p-3 text-sm rounded-lg bg-n-alpha-2"
              >
                <div
                  class="flex flex-wrap items-center justify-between gap-2 text-n-slate-12"
                >
                  <span class="font-medium">{{ attempt.operation }}</span>
                  <span>{{ attempt.status }}</span>
                </div>
                <div class="mt-1 text-n-slate-11">
                  <span v-if="attempt.operation_type">
                    {{ attempt.operation_type }}
                  </span>
                  <span v-if="attempt.created_at">
                    {{ attempt.created_at }}
                  </span>
                </div>
                <div v-if="attempt.error_code" class="mt-1 text-n-ruby-11">
                  {{ attempt.error_code }}
                  <span v-if="attempt.error_message">
                    {{ attempt.error_message }}
                  </span>
                </div>
                <div
                  v-if="formatImportResult(attempt.import_result)"
                  class="mt-1 text-n-slate-11"
                >
                  {{ formatImportResult(attempt.import_result) }}
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="missingScopes.length"
            class="p-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
          >
            <span class="font-medium">
              {{ t('INTEGRATION_SETTINGS.SHOPIFY.STATUS.MISSING_SCOPES') }}
            </span>
            {{ missingScopes.join(', ') }}
          </div>
          <div v-if="statusPayload?.last_error" class="text-sm text-n-ruby-11">
            {{ statusPayload.last_error.message }}
          </div>
          <div
            v-if="isConnected"
            class="grid grid-cols-1 gap-4 p-4 rounded-lg bg-n-alpha-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_8rem_auto] md:items-end"
          >
            <div class="flex flex-col gap-1">
              <label
                class="text-sm font-medium text-n-slate-12"
                for="shopify-targeted-resync-operation"
              >
                {{
                  t('INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.OPERATION')
                }}
              </label>
              <select
                id="shopify-targeted-resync-operation"
                v-model="targetedResyncOperation"
                data-testid="shopify-targeted-resync-operation"
                class="w-full h-10 px-3 text-sm border rounded-lg bg-n-background border-n-weak text-n-slate-12"
                :disabled="isTargetedResyncing || !canStartShopifyProviderWork"
              >
                <option value="product_variant_inventory">
                  {{
                    t(
                      'INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.OPERATIONS.PRODUCT_VARIANT_INVENTORY'
                    )
                  }}
                </option>
                <option value="locations">
                  {{
                    t(
                      'INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.OPERATIONS.LOCATIONS'
                    )
                  }}
                </option>
              </select>
            </div>
            <Input
              v-model="targetedResyncQuery"
              data-testid="shopify-targeted-resync-query"
              :disabled="isTargetedResyncing || !canStartShopifyProviderWork"
              :label="t('INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.QUERY')"
              :placeholder="
                t(
                  'INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.QUERY_PLACEHOLDER'
                )
              "
              :message="
                t('INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.QUERY_HELP')
              "
            />
            <Input
              v-model="targetedResyncGid"
              data-testid="shopify-targeted-resync-gid"
              :disabled="isTargetedResyncing || !canStartShopifyProviderWork"
              :label="t('INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.GID')"
              :placeholder="
                t(
                  'INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.GID_PLACEHOLDER'
                )
              "
              :message="
                t('INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.GID_HELP')
              "
            />
            <Input
              v-model="targetedResyncPageSize"
              data-testid="shopify-targeted-resync-page-size"
              type="number"
              min="1"
              max="250"
              :disabled="isTargetedResyncing || !canStartShopifyProviderWork"
              :label="
                t('INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.PAGE_SIZE')
              "
              :message="
                t('INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.PAGE_SIZE_HELP')
              "
            />
            <Button
              data-testid="shopify-targeted-resync-submit"
              teal
              faded
              :is-loading="isTargetedResyncing"
              :disabled="isTargetedResyncing || !canStartShopifyProviderWork"
              :label="t('INTEGRATION_SETTINGS.SHOPIFY.TARGETED_RESYNC.BUTTON')"
              @click="targetedShopifyResync"
            />
          </div>
          <div
            v-if="isConnected"
            class="grid grid-cols-1 gap-4 p-4 rounded-lg bg-n-alpha-2 md:grid-cols-3 md:items-end"
          >
            <div class="flex flex-col gap-1">
              <label
                class="text-sm font-medium text-n-slate-12"
                for="shopify-replay-event-type"
              >
                {{
                  t('INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.EVENT_TYPE')
                }}
              </label>
              <select
                id="shopify-replay-event-type"
                v-model="replayEventType"
                data-testid="shopify-replay-event-type"
                class="w-full h-10 px-3 text-sm border rounded-lg bg-n-background border-n-weak text-n-slate-12"
                :disabled="isReplayingEnrichment || !isProviderReady"
              >
                <option value="">
                  {{
                    t(
                      'INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.ALL_EVENT_TYPES'
                    )
                  }}
                </option>
                <option
                  v-for="eventTypeOption in replayEventTypeOptions"
                  :key="eventTypeOption.value"
                  :value="eventTypeOption.value"
                >
                  {{ eventTypeOption.label }}
                </option>
              </select>
            </div>
            <div class="flex flex-col gap-1">
              <label
                class="text-sm font-medium text-n-slate-12"
                for="shopify-replay-match-status"
              >
                {{
                  t(
                    'INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.MATCH_STATUS'
                  )
                }}
              </label>
              <select
                id="shopify-replay-match-status"
                v-model="replayMatchStatus"
                data-testid="shopify-replay-match-status"
                class="w-full h-10 px-3 text-sm border rounded-lg bg-n-background border-n-weak text-n-slate-12"
                :disabled="isReplayingEnrichment || !isProviderReady"
              >
                <option value="">
                  {{
                    t(
                      'INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.ALL_MATCH_STATUSES'
                    )
                  }}
                </option>
                <option
                  v-for="matchStatusOption in replayMatchStatusOptions"
                  :key="matchStatusOption.value"
                  :value="matchStatusOption.value"
                >
                  {{ matchStatusOption.label }}
                </option>
              </select>
            </div>
            <Input
              v-model="replaySince"
              data-testid="shopify-replay-since"
              type="date"
              :disabled="isReplayingEnrichment || !isProviderReady"
              :label="t('INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.SINCE')"
              :message="
                t('INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.SINCE_HELP')
              "
            />
            <Input
              v-model="replaySku"
              data-testid="shopify-replay-sku"
              :disabled="isReplayingEnrichment || !isProviderReady"
              :label="t('INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.SKU')"
              :placeholder="
                t(
                  'INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.SKU_PLACEHOLDER'
                )
              "
            />
            <Input
              v-model="replayProductGid"
              data-testid="shopify-replay-product-gid"
              :disabled="isReplayingEnrichment || !isProviderReady"
              :label="
                t('INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.PRODUCT_GID')
              "
              :placeholder="
                t(
                  'INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.PRODUCT_GID_PLACEHOLDER'
                )
              "
            />
            <Input
              v-model="replayVariantGid"
              data-testid="shopify-replay-variant-gid"
              :disabled="isReplayingEnrichment || !isProviderReady"
              :label="
                t('INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.VARIANT_GID')
              "
              :placeholder="
                t(
                  'INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.VARIANT_GID_PLACEHOLDER'
                )
              "
            />
          </div>
          <div
            v-if="isConnected || hasCatalogRows"
            class="flex flex-wrap justify-end gap-3"
          >
            <Button
              v-if="isConnected"
              data-testid="shopify-sync-catalog"
              blue
              faded
              :is-loading="isSyncingCatalog"
              :disabled="isSyncingCatalog || !canStartShopifyProviderWork"
              :label="t('INTEGRATION_SETTINGS.SHOPIFY.SYNC_CATALOG.BUTTON')"
              @click="syncShopifyCatalog"
            />
            <Button
              v-if="isConnected"
              data-testid="shopify-sync-locations"
              blue
              faded
              :is-loading="isSyncingLocations"
              :disabled="isSyncingLocations || !canStartShopifyProviderWork"
              :label="t('INTEGRATION_SETTINGS.SHOPIFY.SYNC_LOCATIONS.BUTTON')"
              @click="syncShopifyLocations"
            />
            <Button
              v-if="isConnected"
              data-testid="shopify-replay-enrichment"
              slate
              faded
              :is-loading="isReplayingEnrichment"
              :disabled="isReplayingEnrichment || !isProviderReady"
              :label="
                t('INTEGRATION_SETTINGS.SHOPIFY.ENRICHMENT_REPLAY.BUTTON')
              "
              @click="replayShopifyEnrichment"
            />
            <Button
              v-if="isConnected"
              data-testid="shopify-test"
              blue
              faded
              :is-loading="isTestingConnection"
              :disabled="isTestingConnection"
              :label="t('INTEGRATION_SETTINGS.SHOPIFY.TEST.BUTTON')"
              @click="testShopifyConnection"
            />
            <Button
              v-if="isConnected && currentBulkOperation"
              data-testid="shopify-refresh-bulk-operation"
              slate
              faded
              :is-loading="isRefreshingBulkOperation"
              :disabled="isRefreshingBulkOperation"
              :label="
                t('INTEGRATION_SETTINGS.SHOPIFY.REFRESH_BULK_OPERATION.BUTTON')
              "
              @click="refreshCurrentBulkOperation"
            />
            <Button
              v-if="isConnected && canCancelCurrentBulkOperation"
              data-testid="shopify-cancel-bulk-operation"
              ruby
              faded
              :is-loading="isCancelingBulkOperation"
              :disabled="
                isCancelingBulkOperation || !canCancelCurrentBulkOperation
              "
              :label="
                t('INTEGRATION_SETTINGS.SHOPIFY.CANCEL_BULK_OPERATION.BUTTON')
              "
              @click="cancelCurrentBulkOperation"
            />
            <Button
              v-if="isConnected"
              data-testid="shopify-disconnect"
              ruby
              faded
              :is-loading="isDisconnecting"
              :disabled="isDisconnecting"
              :label="t('INTEGRATION_SETTINGS.SHOPIFY.DISCONNECT.BUTTON')"
              @click="disconnectShopify"
            />
            <Button
              v-if="hasCatalogRows"
              data-testid="shopify-purge-catalog"
              ruby
              faded
              :is-loading="isPurgingCatalog"
              :disabled="isPurgingCatalog"
              :label="t('INTEGRATION_SETTINGS.SHOPIFY.PURGE.BUTTON')"
              @click="openPurgeCatalogDialog"
            />
          </div>
        </section>
        <div
          v-if="error"
          class="flex items-center justify-center flex-1 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow p-6"
        >
          <p class="text-n-ruby-9">
            {{ t('INTEGRATION_SETTINGS.SHOPIFY.ERROR') }}
          </p>
        </div>
        <Dialog
          ref="dialogRef"
          :title="t('INTEGRATION_SETTINGS.SHOPIFY.STORE_URL.TITLE')"
          :description="
            t('INTEGRATION_SETTINGS.SHOPIFY.CREDENTIALS.DESCRIPTION')
          "
          :confirm-button-label="
            t('INTEGRATION_SETTINGS.SHOPIFY.STORE_URL.SUBMIT')
          "
          :cancel-button-label="
            t('INTEGRATION_SETTINGS.SHOPIFY.STORE_URL.CANCEL')
          "
          :is-loading="isSubmitting"
          @confirm="handleStoreUrlSubmit"
          @close="hideStoreUrlModal"
        >
          <div class="flex flex-col gap-4">
            <div
              v-if="setupCallbackUrl || setupRequiredScopesLabel"
              data-testid="shopify-custom-app-setup"
              class="p-3 text-sm rounded-lg bg-n-alpha-2 text-n-slate-12"
            >
              <div class="font-medium">
                {{ t('INTEGRATION_SETTINGS.SHOPIFY.CREDENTIALS.APP_SETUP') }}
              </div>
              <dl class="grid grid-cols-1 gap-2 mt-2">
                <div v-if="setupCallbackUrl">
                  <dt class="text-n-slate-11">
                    {{
                      t('INTEGRATION_SETTINGS.SHOPIFY.CREDENTIALS.CALLBACK_URL')
                    }}
                  </dt>
                  <dd class="m-0 break-all">{{ setupCallbackUrl }}</dd>
                </div>
                <div v-if="setupRequiredScopesLabel">
                  <dt class="text-n-slate-11">
                    {{
                      t(
                        'INTEGRATION_SETTINGS.SHOPIFY.CREDENTIALS.REQUIRED_SCOPES'
                      )
                    }}
                  </dt>
                  <dd class="m-0 break-all">{{ setupRequiredScopesLabel }}</dd>
                </div>
              </dl>
            </div>
            <Input
              v-model="storeUrl"
              :label="t('INTEGRATION_SETTINGS.SHOPIFY.STORE_URL.LABEL')"
              :placeholder="
                t('INTEGRATION_SETTINGS.SHOPIFY.STORE_URL.PLACEHOLDER')
              "
              :message="
                !storeUrlError
                  ? t('INTEGRATION_SETTINGS.SHOPIFY.STORE_URL.HELP')
                  : storeUrlError
              "
              :message-type="storeUrlError ? 'error' : 'info'"
            />
            <Input
              v-model="clientId"
              :label="t('INTEGRATION_SETTINGS.SHOPIFY.CREDENTIALS.CLIENT_ID')"
              :placeholder="
                t(
                  'INTEGRATION_SETTINGS.SHOPIFY.CREDENTIALS.CLIENT_ID_PLACEHOLDER'
                )
              "
              :message="
                !clientIdError
                  ? t('INTEGRATION_SETTINGS.SHOPIFY.CREDENTIALS.CLIENT_ID_HELP')
                  : clientIdError
              "
              :message-type="clientIdError ? 'error' : 'info'"
            />
            <Input
              v-model="clientSecret"
              type="password"
              :label="
                t('INTEGRATION_SETTINGS.SHOPIFY.CREDENTIALS.CLIENT_SECRET')
              "
              :placeholder="
                t(
                  'INTEGRATION_SETTINGS.SHOPIFY.CREDENTIALS.CLIENT_SECRET_PLACEHOLDER'
                )
              "
              :message="
                !clientSecretError
                  ? t(
                      'INTEGRATION_SETTINGS.SHOPIFY.CREDENTIALS.CLIENT_SECRET_HELP'
                    )
                  : clientSecretError
              "
              :message-type="clientSecretError ? 'error' : 'info'"
            />
          </div>
        </Dialog>
        <Dialog
          ref="purgeDialogRef"
          :title="t('INTEGRATION_SETTINGS.SHOPIFY.PURGE.TITLE')"
          :description="t('INTEGRATION_SETTINGS.SHOPIFY.PURGE.MESSAGE')"
          :confirm-button-label="
            t('INTEGRATION_SETTINGS.SHOPIFY.PURGE.CONFIRM')
          "
          :cancel-button-label="
            t('INTEGRATION_SETTINGS.SHOPIFY.STORE_URL.CANCEL')
          "
          :is-loading="isPurgingCatalog"
          @confirm="purgeShopifyCatalog"
        >
          <p class="m-0 text-sm text-n-ruby-11">
            {{ t('INTEGRATION_SETTINGS.SHOPIFY.PURGE.WARNING') }}
          </p>
        </Dialog>
      </div>
    </template>
  </SettingsLayout>
</template>
